import { api } from '@/lib/api.js';
import { keepRecordValue } from '@/services/form-differs.service.js';

// What's-waiting queue (MV-04 2.1 + MV-10 3.1, Ship 4a). Items are DERIVED
// server-side; resolving the underlying cause clears them. The only stored
// rows are pure notifications, cleared via resolveQueueNotification.

export function getQueue({ allBrokers = false, dealId = null } = {}) {
  const params = new URLSearchParams();
  if (allBrokers) params.set('all_brokers', '1');
  if (dealId) params.set('deal', dealId);
  params.set('_t', Date.now());
  return api.get(`/api/tracon/queue?${params.toString()}`);
}

export function resolveQueueNotification(id) {
  return api.post(`/api/tracon/queue/notifications/${encodeURIComponent(id)}/resolve`);
}

// A possible match (R6, PLAN-ROUND3 1.3): David answers 'same' or 'different' at
// its own door (routes/tracon-possible-matches.routes.js), which records the
// answer only and reads the row back. Its refusals are expected answers the
// caller says itself, so api.js does not toast twice.
const DECIDE_ANSWERS = [
  { status: 409, code: 'already_answered' },
  { status: 409, code: 'wrong_kind' },
  { status: 404, code: 'not_found' },
  { status: 400, code: 'bad_answer' },
];
export function decidePossibleMatch(id, answer) {
  return api.post(`/api/tracon/possible-matches/${encodeURIComponent(id)}/decide`, { answer }, { expected: DECIDE_ANSWERS });
}

// The clearing button of a stored notification. A pilot_form_differs flag's
// button says Keep mine and goes through its own door (R4, T8e), which checks
// the kind and records David's decision. A possible_match row is never cleared:
// it is answered through its decide door, and without an answer nothing is sent
// (the generic resolve refuses the kind, 409 use_decide_door). Every other kind
// clears as before.
export function clearQueueNotification(item, { answer } = {}) {
  const id = item.refs.notification_id;
  if (item.kind === 'possible_match') {
    if (answer !== 'same' && answer !== 'different') return Promise.reject(new Error('A possible match is answered Same person or Not the same'));
    return decidePossibleMatch(id, answer);
  }
  return item.kind === 'pilot_form_differs' ? keepRecordValue(id) : resolveQueueNotification(id);
}

// Attach a paper PHF that arrived with no deal. Sets session_id on the row so
// it joins that deal's Pilots panel and goes through the SAME Confirm button as
// every other paper PHF. Does NOT add the pilot to the roster or CRM.
export function attachPaperPhf(phfId, dealId) {
  return api.post(`/api/tracon/paper-phfs/${encodeURIComponent(phfId)}/attach`, { deal_id: dealId });
}

// One door per kind (MV-10 3.1 GUARD: the queue item click lands on the
// exact door, never a generic page). Deal-scoped doors ride the account
// workspace deep link with ?door= (DealWorkspace opens the matching sheet).
export function doorForItem(item) {
  const dealHref = item.deal_id
    ? `/account/${encodeURIComponent(item.deal_id)}/deal/${encodeURIComponent(item.deal_id)}`
    : null;
  switch (item.kind) {
    case 'paper_draft':
      return { label: 'Review paperwork', href: dealHref ? `${dealHref}?door=paperwork` : '/quote-inbox' };
    case 'quote_draft':
      return { label: 'Review parse', href: `/quote-inbox?review=${encodeURIComponent(item.refs?.quote_id || '')}` };
    case 'quote_triage':
      return { label: 'Pick the deal', href: '/quote-inbox' };
    case 'quote_mail':
      return { label: 'Review parse', href: '/quote-inbox' };
    case 'nudge_due':
      return { label: 'Draft follow-up', href: `/follow-ups?nudge=${encodeURIComponent(item.refs?.market_id || '')}` };
    // Money David parked. The door is the deal's money strip, where the
    // Deferred tile and his own reason already live, so the item explains
    // itself the moment he lands.
    case 'deferred_collection':
      return { label: 'Open the money', href: dealHref ? `${dealHref}?door=money` : '/queue' };
    case 'quote_expiring':
      return { label: 'Open deal', href: dealHref ? `${dealHref}?door=quotes` : '/follow-ups' };
    case 'escalation':
      return { label: 'Decide routing', href: dealHref ? `${dealHref}?door=rtq` : '/command-center' };
    case 'parked_intake':
      return { label: 'Send resume link', href: dealHref ? `${dealHref}?door=resume_link` : '/traffic' };
    // Both paper-PHF kinds are deal-less by definition, so there is no door to
    // land on. The unattached row carries its own inline attach control (that
    // IS its action); the unlanded row is a failure notice whose recovery
    // handles live in the broker alert email.
    case 'paper_phf_unattached':
    case 'paper_phf_unlanded':
      return null;
    // A submitted pilot with no CRM contact. The door is the deal's roster
    // panel: its holding strip carries the Confirm and Dismiss buttons.
    case 'pilot_unlinked':
      return { label: 'Open the roster', href: dealHref ? `${dealHref}?door=roster` : '/queue' };
    // Decision 8: a held signing lands on the hold sheet (openers.bind_hold),
    // where David releases (quote stands) or re-quotes. A failed hold read has
    // no door: there is nothing to open until the read succeeds.
    case 'bind_held':
      return { label: 'Review the hold', href: dealHref ? `${dealHref}?door=bind_hold` : '/queue' };
    case 'bind_holds_unreadable':
      return null;
    // A carrier send the liability floor held (David 2026-11-03): the door is where the limit is set, the deal's
    // roster sheet (the floor's own remedy link, which names the account).
    case 'carrier_send_held':
      return { label: 'Set the limit', href: item.refs?.remedy_link || (dealHref ? `${dealHref}?door=edit_roster` : '/queue') };
    // A possible match (R6) is answered on its own row (Same person / Not the
    // same); there is no other screen to land on, so there is no door.
    case 'possible_match':
      return null;
    // The differing signed-application links (deals-door-1 round 3, R2d-Q2): one deal-less item; the door is the review page.
    case 'signed_app_link_review':
      return { label: 'Review the links', href: '/signed-applications/review' };
    // paperwork-1 L4-2: the WorkDrive imports not yet named or confirmed; the door is screen 9.
    case 'imported_documents_to_name':
      return { label: 'Name the imported documents', href: '/documents/imports' };
    // A phone call (David 2026-10-19) carries no deal. The door is where the
    // caller landed: the Account the call filed to, else the Contact's
    // conversation. A people suggestion (or the call record alone) has no
    // TRACON screen yet, so there is no door; the line says where it landed.
    case 'phone_intake_call':
      if (item.refs?.crm_account_id) return { label: 'Open the account', href: `/account/${encodeURIComponent(item.refs.crm_account_id)}` };
      if (item.refs?.crm_contact_id) return { label: 'Open the conversation', href: `/traffic/conversation/${encodeURIComponent(item.refs.crm_contact_id)}` };
      return null;
    case 'payment_cleared':
    case 'signature_completed':
    case 'phf_submitted':
    case 'awaiting_client':
    case 'market_waiting':
    default:
      return dealHref ? { label: 'Open deal', href: dealHref } : null;
  }
}
